import {
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
  DropdownButtonProps,
  Tooltip,
} from '@dixa/lib-shared-habla';

import { appActions } from 'app/app.actions';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { SavedSearchFormModalType } from '../form/savedSearchFormModal.types';

type SavedSearchButtonProps = Partial<DropdownButtonProps>;

/**
 * Represents a dropdown button component that is used to access save search
 * actions
 */
export const SavedSearchButton = (
  props: SavedSearchButtonProps,
): JSX.Element => {
  const dispatch = useAppDispatch();
  const { params, savedSearch } = useAppSelector((state) => state.search);

  const flags = useFeatureFlag(['new-search-conversations-view']);
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const isCompact = isSmallScreen || flags.newSearchConversationsView;

  const handleOpenSavedSearchListModal = () => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'search:savedSearch:list',
        },
      }),
    );
  };

  const handleOpenSavedSearchFormModal = (mode: SavedSearchFormModalType) => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'search:savedSearch:form',
          arg: {
            mode,
          },
        },
      }),
    );
  };

  const hasSearchQuery = !!params.query;
  const hasFilters = params.filters.filters.length > 0;

  return (
    <Tooltip content="Saved searches" disabled={!isCompact}>
      <DropdownButton
        {...props}
        buttonProps={{
          type: 'button',
          ...props.buttonProps,
        }}
        icon={isCompact ? 'bookmark' : undefined}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ toggleDropdown }) => (
              <ButtonList>
                <ButtonList.Item
                  icon={savedSearch ? 'upload' : 'search-plus'}
                  isDisabled={!hasSearchQuery && !hasFilters}
                  onClick={() => {
                    toggleDropdown();

                    handleOpenSavedSearchFormModal(
                      savedSearch ? 'update' : 'create',
                    );
                  }}
                  buttonProps={{
                    type: 'button',
                  }}
                >
                  {savedSearch ? 'Update existing' : 'Save new search'}
                </ButtonList.Item>

                {savedSearch && (
                  <>
                    <ButtonList.Item
                      icon="search-plus"
                      onClick={() => {
                        toggleDropdown();

                        handleOpenSavedSearchFormModal('duplicate');
                      }}
                      buttonProps={{
                        type: 'button',
                      }}
                    >
                      Save as new search
                    </ButtonList.Item>

                    <ButtonList.Divider />
                  </>
                )}

                <ButtonList.Item
                  icon="redo"
                  onClick={() => {
                    toggleDropdown();
                    handleOpenSavedSearchListModal();
                  }}
                  buttonProps={{
                    type: 'button',
                  }}
                >
                  Apply saved search
                </ButtonList.Item>
              </ButtonList>
            )}
          </DropdownButtonContext.Consumer>
        }
        data-testid="saved-search-dropdown"
      >
        {isCompact ? null : 'Saved searches'}
      </DropdownButton>
    </Tooltip>
  );
};
